import imageCompression from 'browser-image-compression';

export const compressReceipt = async (file) => {
  const options = {
    maxSizeMB: 0.2,              // Target 200KB max
    maxWidthOrHeight: 1000,      // Receipts don't need high res
    useWebWorker: true,
    fileType: 'image/jpeg',      // Force JPEG
    quality: 0.6,                // 60% quality sufficient for receipts
    initialQuality: 0.6
  };
  
  try {
    return await imageCompression(file, options);
  } catch (error) {
    console.error('Compression failed:', error);
    throw new Error('Failed to compress image');
  }
};

export const extractReceiptText = async (imageFile, options = {}) => {
  console.log('Starting OCR with file:', imageFile);
  console.log('File type:', imageFile.type);
  console.log('File size:', imageFile.size);
  
  try {
    // Try different import approaches for Tesseract
    console.log('Attempting to import Tesseract...');
    let Tesseract;
    
    try {
      // Method 1: Direct import
      Tesseract = await import('tesseract.js');
      console.log('Tesseract imported via direct import:', Tesseract);
    } catch (importError) {
      console.warn('Direct import failed, trying alternative:', importError);
      // Method 2: Try accessing from window if globally loaded
      if (window.Tesseract) {
        Tesseract = window.Tesseract;
        console.log('Using window.Tesseract:', Tesseract);
      } else {
        throw new Error('Tesseract.js not available');
      }
    }
    
    // Check for createWorker function
    const createWorker = Tesseract.createWorker || Tesseract.default?.createWorker;
    if (!createWorker) {
      console.error('createWorker not found. Available methods:', Object.keys(Tesseract));
      throw new Error('createWorker function not available');
    }
    
    console.log('Creating worker...');
    const worker = await createWorker();
    console.log('Worker created successfully');
    
    try {
      console.log('Initializing worker...');
      await worker.loadLanguage('eng');
      await worker.initialize('eng');
      console.log('Worker initialized successfully');
      
      console.log('Setting parameters...');
      await worker.setParameters({
        tessedit_char_whitelist: '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz£$€.,:-/ ()',
        tessedit_pageseg_mode: '6',
        tessedit_ocr_engine_mode: '1'
      });
      console.log('Parameters set successfully');
      
      console.log('Starting recognition...');
      const result = await worker.recognize(imageFile);
      console.log('Recognition complete. Result:', result);
      
      if (!result || !result.data) {
        throw new Error('Invalid OCR result structure');
      }
      
      const { data: { text, confidence, words } } = result;
      console.log('OCR Raw Text:', text);
      console.log('OCR Confidence:', confidence);
      console.log('OCR Words count:', words?.length || 0);
      
      // Extract key receipt data with multiple strategies
      const extractedData = parseReceiptText(text, words);
      
      return {
        text: text || '',
        confidence: confidence || 0,
        words: words || [],
        amount: extractedData.amount,
        date: extractedData.date,
        vendor: extractedData.vendor,
        allAmounts: extractedData.allAmounts
      };
    } finally {
      console.log('Terminating worker...');
      try {
        await worker.terminate();
        console.log('Worker terminated successfully');
      } catch (termError) {
        console.warn('Worker termination error:', termError);
      }
    }
  } catch (error) {
    console.error('OCR detailed error:', {
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    
    // Return a more detailed error result
    return {
      text: `OCR initialization failed: ${error.message}`,
      confidence: 0,
      words: [],
      amount: null,
      date: null,
      vendor: null,
      allAmounts: [],
      error: `OCR failed: ${error.message}`,
      errorDetails: {
        name: error.name,
        message: error.message,
        stack: error.stack
      }
    };
  }
};

export const testTesseract = async () => {
  try {
    console.log('Testing Tesseract import...');
    
    // Try multiple import methods
    let Tesseract;
    try {
      Tesseract = await import('tesseract.js');
      console.log('Direct import successful:', Tesseract);
    } catch (importError) {
      console.warn('Direct import failed:', importError);
      if (window.Tesseract) {
        Tesseract = window.Tesseract;
        console.log('Using window.Tesseract');
      } else {
        return { 
          success: false, 
          message: 'Tesseract.js import failed', 
          error: importError.message 
        };
      }
    }
    
    console.log('Available Tesseract methods:', Object.keys(Tesseract));
    
    const createWorker = Tesseract.createWorker || Tesseract.default?.createWorker;
    if (createWorker) {
      console.log('createWorker is available, testing worker creation...');
      try {
        const worker = await createWorker();
        console.log('Test worker created successfully');
        await worker.loadLanguage('eng');
        console.log('Language loaded successfully');
        await worker.initialize('eng');
        console.log('Worker initialized successfully');
        await worker.terminate();
        console.log('Test worker terminated successfully');
        return { 
          success: true, 
          message: 'Tesseract is working correctly' 
        };
      } catch (workerError) {
        console.error('Worker test failed:', workerError);
        return { 
          success: false, 
          message: `Worker test failed: ${workerError.message}`, 
          error: workerError 
        };
      }
    } else {
      return { 
        success: false, 
        message: 'createWorker function not found' 
      };
    }
  } catch (error) {
    console.error('Tesseract test failed:', error);
    return { 
      success: false, 
      message: `Test failed: ${error.message}`, 
      error 
    };
  }
};

const parseReceiptText = (text, words = []) => {
  console.log('Parsing receipt text:', text);
  
  // More aggressive amount detection strategies
  const amountStrategies = [
    // Strategy 1: Look for "Total", "Amount", "Paid" etc. (highest priority)
    /(?:total|amount|sum|paid|due|charge)[\s:£$]*(\d+[.,]\d{2})/i,
    
    // Strategy 2: Look for "PAID" specifically with flexible spacing
    /paid[\s:]*[£$€]?\s*(\d+[.,]\d{2})/i,
    
    // Strategy 3: Look for currency symbols followed by amounts
    /[£$€]\s*(\d+[.,]\d{2})/g,
    
    // Strategy 4: Look for amounts at end of lines (common on receipts)
    /(\d+[.,]\d{2})\s*$/gm,
    
    // Strategy 5: Look for standalone amounts (2+ digits with decimal)
    /\b(\d{2,}[.,]\d{2})\b/g,
    
    // Strategy 6: Look for specific receipt total patterns
    /(?:grand\s*total|subtotal|net\s*amount|balance)[\s:£$]*(\d+[.,]\d{2})/i,
    
    // Strategy 7: Very flexible - any amount pattern
    /(\d+[.,]\d{2})/g
  ];
  
  let allAmounts = [];
  let bestAmount = null;
  let confidenceScore = 0;
  
  // Try each strategy
  amountStrategies.forEach((regex, index) => {
    let matches;
    if (regex.global) {
      matches = [...text.matchAll(regex)];
    } else {
      const match = text.match(regex);
      matches = match ? [match] : [];
    }
    
    matches.forEach(match => {
      const amountStr = match[1] || match[0];
      const cleanAmountStr = amountStr.replace(/[£$€]/g, '').replace(',', '.');
      const amount = parseFloat(cleanAmountStr);
      
      if (!isNaN(amount) && amount > 0) {
        const confidence = calculateAmountConfidence(match, text, index);
        allAmounts.push({
          amount,
          originalText: match[0],
          strategy: index + 1,
          confidence,
          matchIndex: match.index
        });
        
        if (confidence > confidenceScore) {
          bestAmount = amount;
          confidenceScore = confidence;
        }
      }
    });
  });
  
  // Remove duplicates and sort by confidence
  allAmounts = removeDuplicateAmounts(allAmounts);
  
  console.log('All detected amounts:', allAmounts);
  console.log('Best amount:', bestAmount, 'with confidence:', confidenceScore);
  
  // Enhanced date extraction patterns
  const datePatterns = [
    /(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4})/,
    /(\d{1,2}\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\s+\d{2,4})/i,
    /(\d{4}[\/\-\.]\d{1,2}[\/\-\.]\d{1,2})/,
    /(\d{1,2}(?:st|nd|rd|th)?\s+(?:january|february|march|april|may|june|july|august|september|october|november|december)\s+\d{2,4})/i
  ];
  
  let detectedDate = null;
  for (const pattern of datePatterns) {
    const match = text.match(pattern);
    if (match) {
      detectedDate = match[1];
      break;
    }
  }
  
  // Enhanced vendor extraction (first non-numeric lines)
  const lines = text.split('\n').filter(line => line.trim());
  const vendorCandidates = lines.slice(0, 5) // Check first 5 lines
    .filter(line => {
      const trimmed = line.trim();
      return trimmed.length > 2 && 
             !/^\d+/.test(trimmed) && // Not starting with numbers
             !/^\d+[.,]\d{2}$/.test(trimmed) && // Not just an amount
             !/(total|amount|paid|due)/i.test(trimmed); // Not amount-related keywords
    })
    .map(line => line.trim());
  
  return {
    amount: bestAmount,
    allAmounts,
    date: detectedDate,
    vendor: vendorCandidates[0] || null
  };
};

// Enhanced confidence calculation
const calculateAmountConfidence = (match, fullText, strategyIndex) => {
  let confidence = 0;
  const matchText = match[0].toLowerCase();
  const beforeMatch = fullText.substring(0, match.index).toLowerCase();
  const afterMatch = fullText.substring(match.index + match[0].length).toLowerCase();
  
  // Keyword-based confidence (higher for specific terms)
  if (matchText.includes('total')) confidence += 50;
  if (matchText.includes('paid')) confidence += 45; // Higher for "paid"
  if (matchText.includes('amount')) confidence += 40;
  if (matchText.includes('due')) confidence += 35;
  if (matchText.includes('charge')) confidence += 30;
  
  // Context-based confidence
  if (/\b(total|grand|final)\b/i.test(beforeMatch)) confidence += 25;
  if (/\b(paid|payment|amount)\b/i.test(beforeMatch)) confidence += 20;
  
  // Position-based confidence (amounts near end are often totals)
  const position = match.index / fullText.length;
  if (position > 0.8) confidence += 25; // Very near end
  else if (position > 0.6) confidence += 15; // Near end
  else if (position > 0.4) confidence += 5; // Middle-ish
  
  // Currency symbol presence
  if (matchText.includes('£') || matchText.includes('$') || matchText.includes('€')) {
    confidence += 20;
  }
  
  // Strategy priority (earlier strategies are more targeted)
  confidence += (8 - strategyIndex) * 3;
  
  // Amount value analysis
  const amount = parseFloat((match[1] || match[0]).replace(/[£$€,]/g, ''));
  if (amount < 0.50) confidence -= 30; // Very small amounts unlikely to be totals
  else if (amount < 2) confidence -= 15; // Small amounts less likely
  else if (amount > 10000) confidence -= 20; // Very large amounts less common
  else if (amount >= 5 && amount <= 500) confidence += 10; // Common receipt range
  
  // Line context analysis
  const lineStart = fullText.lastIndexOf('\n', match.index);
  const lineEnd = fullText.indexOf('\n', match.index);
  const currentLine = fullText.substring(lineStart + 1, lineEnd === -1 ? fullText.length : lineEnd);
  
  // If amount is alone on line or at end of line, higher confidence
  if (currentLine.trim() === match[0].trim()) confidence += 15;
  if (currentLine.trim().endsWith(match[0].trim())) confidence += 10;
  
  return Math.max(0, confidence);
};

// Remove duplicate amounts (within 0.01 difference)
const removeDuplicateAmounts = (amounts) => {
  const unique = [];
  amounts.forEach(newAmount => {
    const isDuplicate = unique.some(existing => 
      Math.abs(existing.amount - newAmount.amount) < 0.01
    );
    if (!isDuplicate) {
      unique.push(newAmount);
    }
  });
  return unique.sort((a, b) => b.confidence - a.confidence);
};
